import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Progress, Card, Spinner } from '@heroui/react';
import { Hash, type Address } from 'viem';
import toast from '@/util/toast';
import { waitForTransactionReceipt } from '@wagmi/core';
import { config as wagmiConfig } from '@/config/index';
import { txLink } from '@/util/explorerLinks';

// Type for the asset being approved (ERC20 token or NFT)
export enum AssetType {
  ERC20 = 'ERC20',
  ERC721 = 'ERC721',
  ERC1155 = 'ERC1155',
}

// Status of the approval flow
export enum ApprovalStatus {
  IDLE = 'idle',
  CHECKING = 'checking',
  NEEDS_APPROVAL = 'needs_approval',
  APPROVING = 'approving',
  APPROVED = 'approved',
  ERROR = 'error',
}

// Transaction steps
export type TransactionStep = 'approval' | 'main' | 'complete';

// Configuration for the approval flow
export interface ApprovalConfig {
  assetType: AssetType;
  assetAddress: Address;
  spenderAddress: Address;
  chainId: number;
  amount?: bigint;
  tokenId?: bigint;
  assetName?: string;
  assetSymbol?: string;
  assetIcon?: string;
  // Custom text for each step
  text: {
    modalTitle: {
      approval: string;
      main: string;
      complete: string;
    };
    steps: {
      approval: string;
      main: string;
    };
    messages: {
      checking: string;
      needsApproval: string;
      approving: string;
      approved: string;
      mainAction: string;
      complete: string;
    };
    buttons: {
      approve: string;
      executeMain: string;
      close: string;
    };
  };
}

// Props for the ApprovalTransactionFlow component
interface ApprovalTransactionFlowProps {
  config: ApprovalConfig;
  isOpen: boolean;
  onClose: () => void;
  // Function to check if approval is needed
  checkNeedsApproval: (params: {
    assetAddress: Address;
    spenderAddress: Address;
    amount?: bigint;
    tokenId?: bigint;
    assetType: AssetType;
    chainId: number;
  }) => Promise<boolean>;
  // Function to execute the approval transaction
  executeApproval: (params: {
    assetAddress: Address;
    spenderAddress: Address;
    amount?: bigint;
    tokenId?: bigint;
    assetType: AssetType;
    chainId: number;
  }) => Promise<{
    hash?: string;
    success: boolean;
  }>;
  // Function to execute the main transaction after approval (if needed)
  executeMainTransaction: () => Promise<{
    hash?: string;
    success: boolean;
  }>;
  // Optional callbacks
  onApprovalStart?: () => void;
  onApprovalSuccess?: () => void;
  onApprovalError?: (error: Error) => void;
  onMainTransactionStart?: () => void;
  onMainTransactionSuccess?: () => void;
  onMainTransactionError?: (error: Error) => void;
  onComplete?: () => void;
}
export const ApprovalTransactionFlow: React.FC<ApprovalTransactionFlowProps> = ({
  config,
  isOpen,
  onClose,
  checkNeedsApproval,
  executeApproval,
  executeMainTransaction,
  onApprovalStart,
  onApprovalSuccess,
  onApprovalError,
  onMainTransactionStart,
  onMainTransactionSuccess,
  onMainTransactionError,
  onComplete
}) => {
  // State for tracking the approval flow
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>(ApprovalStatus.IDLE);
  const [txStep, setTxStep] = useState<TransactionStep>('approval');
  const [txProgress, setTxProgress] = useState<number>(0);
  const [txMessage, setTxMessage] = useState<string>('');
  const [needsApproval, setNeedsApproval] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [approvalTxHash, setApprovalTxHash] = useState<string | null>(null);
  const [mainTxHash, setMainTxHash] = useState<string | null>(null);

  // Helper function to get asset name for display
  const getAssetName = () => {
    if (config.assetName) return config.assetName;
    if (config.assetSymbol) return config.assetSymbol;
    return config.assetType === AssetType.ERC20 ? 'token' : 'NFT';
  };

  // Check if approval is needed when component mounts or when dependencies change
  const checkApprovalStatus = useCallback(async () => {
    if (!isOpen) return;
    try {
      setApprovalStatus(ApprovalStatus.CHECKING);
      setTxMessage(config.text.messages.checking);
      const needsApproval_0 = await checkNeedsApproval({
        assetAddress: config.assetAddress,
        spenderAddress: config.spenderAddress,
        amount: config.amount,
        tokenId: config.tokenId,
        assetType: config.assetType,
        chainId: config.chainId
      });
      console.log('ApprovalTransactionFlow - Check approval result:', {
        assetType: config.assetType,
        assetAddress: config.assetAddress,
        spender: config.spenderAddress,
        tokenId: config.tokenId,
        needsApproval: needsApproval_0
      });
      setNeedsApproval(needsApproval_0);
      setApprovalStatus(needsApproval_0 ? ApprovalStatus.NEEDS_APPROVAL : ApprovalStatus.APPROVED);
      setTxMessage(needsApproval_0 ? config.text.messages.needsApproval : config.text.messages.approved);

      // If no approval needed, set progress to 50% and move to main transaction
      if (!needsApproval_0) {
        setTxProgress(50);
        setTxStep('main');
      }
    } catch (err) {
      console.error('Error checking approval status:', err);
      setError('Failed to check approval status. Please try again.');
      setApprovalStatus(ApprovalStatus.ERROR);
    }
  }, [isOpen, config.assetAddress, config.spenderAddress, config.amount, config.tokenId, config.assetType, config.chainId, config.text.messages.checking, config.text.messages.needsApproval, config.text.messages.approved, checkNeedsApproval]);

  // Check approval status when component mounts or modal opens
  useEffect(() => {
    if (isOpen && approvalStatus === ApprovalStatus.IDLE) {
      checkApprovalStatus();
    }
  }, [isOpen, approvalStatus, checkApprovalStatus]);

  // Handle approval transaction
  const handleApprove = async () => {
    if (approvalStatus === ApprovalStatus.APPROVING) return;
    try {
      // Call the onApprovalStart callback if provided
      onApprovalStart?.();
      setApprovalStatus(ApprovalStatus.APPROVING);
      setTxProgress(10);
      setTxMessage(config.text.messages.approving);
      const {
        hash,
        success
      } = await executeApproval({
        assetAddress: config.assetAddress,
        spenderAddress: config.spenderAddress,
        amount: config.amount,
        tokenId: config.tokenId,
        assetType: config.assetType,
        chainId: config.chainId
      });
      if (!success) {
        throw new Error(`Failed to approve ${getAssetName()}`);
      }

      // Set the approval transaction hash
      if (hash) {
        setApprovalTxHash(hash);

        // Wait for transaction confirmation
        setTxProgress(30);
        setTxMessage(`Approval transaction submitted. Waiting for confirmation...`);
        const receipt = await waitForTransactionReceipt(wagmiConfig, {
          hash: hash as `0x${string}`,
          chainId: config.chainId
        });
        if (receipt.status !== 'success') {
          throw new Error(`Transaction failed with status: ${receipt.status}`);
        }
      }

      // Approval succeeded
      setTxProgress(50);
      setTxMessage(config.text.messages.approved);
      setApprovalStatus(ApprovalStatus.APPROVED);
      setNeedsApproval(false);

      // Call the onApprovalSuccess callback if provided
      onApprovalSuccess?.();

      // Move to main transaction step after a short delay
      setTimeout(() => {
        setTxStep('main');
      }, 1000);
    } catch (err_0) {
      console.error('Error approving:', err_0);
      const errorMessage = err_0 instanceof Error ? err_0.message : 'Unknown error occurred';
      setError(`Error approving ${getAssetName()}: ${errorMessage}`);
      setApprovalStatus(ApprovalStatus.ERROR);

      // Call the onApprovalError callback if provided
      if (onApprovalError && err_0 instanceof Error) {
        onApprovalError(err_0);
      }
    }
  };

  // Handle main transaction
  const handleMainTransaction = async () => {
    try {
      // Call the onMainTransactionStart callback if provided
      onMainTransactionStart?.();
      setTxProgress(60);
      setTxMessage(config.text.messages.mainAction);
      const {
        hash: hash_0,
        success: success_0
      } = await executeMainTransaction();
      if (!success_0) {
        throw new Error('Main transaction failed');
      }

      // Set the main transaction hash
      if (hash_0) {
        setMainTxHash(hash_0);

        // Wait for transaction confirmation
        setTxProgress(80);
        setTxMessage('Transaction submitted. Waiting for confirmation...');
        const receipt_0 = await waitForTransactionReceipt(wagmiConfig, {
          hash: hash_0 as Hash,
          chainId: config.chainId
        });
        if (receipt_0.status === 'reverted') {
          throw new Error(`Transaction failed with status: ${receipt_0.status}`);
        }
      }

      // Transaction complete
      setTxProgress(100);
      setTxStep('complete');
      setTxMessage(config.text.messages.complete);

      // Call the onMainTransactionSuccess callback if provided
      onMainTransactionSuccess?.();

      // Call the onComplete callback if provided
      onComplete?.();
    } catch (err_1) {
      console.error('Error executing main transaction:', err_1);
      const errorMessage_0 = err_1 instanceof Error ? err_1.message : 'Unknown error occurred';
      setError(`Error executing transaction: ${errorMessage_0}`);

      // Call the onMainTransactionError callback if provided
      if (onMainTransactionError && err_1 instanceof Error) {
        onMainTransactionError(err_1);
      }
    }
  };

  // Auto-execute main transaction if approval is not needed or after approval
  useEffect(() => {
    if (isOpen && txStep === 'main' && approvalStatus === ApprovalStatus.APPROVED && txProgress === 50) {
      handleMainTransaction();
    }
  }, [isOpen, txStep, approvalStatus, txProgress]);

  // Function to handle modal close
  const handleClose = () => {
    // Only allow closing if transaction is complete or errored
    if (txProgress === 100 || error) {
      setApprovalStatus(ApprovalStatus.IDLE);
      setTxStep('approval');
      setTxProgress(0);
      setTxMessage('');
      setNeedsApproval(false);
      setError(null);
      setApprovalTxHash(null);
      setMainTxHash(null);
      onClose();
    }
  };
  return <Modal classNames={{
    base: 'border-1 border-white bg-black/90'
  }} isOpen={isOpen} onClose={handleClose} size="md" backdrop="blur" hideCloseButton={txProgress > 0 && txProgress < 100 && !error}>
      <ModalContent>
        <ModalHeader>
          <h3 className="text-xl font-semibold">
            {txStep === 'approval' ? config.text.modalTitle.approval : txStep === 'main' ? config.text.modalTitle.main : config.text.modalTitle.complete}
          </h3>
        </ModalHeader>
        
        <ModalBody>
          <div className="space-y-4">
            {/* Error display */}
            {error && <Card className="bg-danger-100 border-danger p-4 mb-4">
                <div className="text-danger font-semibold">Error</div>
                <div className="text-sm">{error}</div>
              </Card>}
            
            {/* Transaction steps */}
            <div className="flex items-center justify-between mb-4">
              {/* Approval step */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${needsApproval ? txStep === 'approval' ? 'bg-primary text-white' : txProgress >= 50 ? 'bg-success text-white' : 'bg-gray-200' : 'bg-gray-200 text-black'}`}>
                  {txProgress >= 50 && needsApproval ? '✓' : '1'}
                </div>
                <span className="text-sm mt-1">{config.text.steps.approval}</span>
              </div>

              {/* Progress bar */}
              <div className="flex-1 h-1 mx-2">
                <Progress classNames={{
                base: 'max-w-md',
                track: 'drop-shadow-md border border-default',
                indicator: 'bg-gradient-to-r from-pink-500 to-yellow-500',
                label: 'tracking-wider font-medium text-default-600',
                value: 'text-foreground/60'
              }} value={txProgress} color={txProgress === 100 ? 'success' : 'primary'} />
              </div>

              {/* Main transaction step */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${txStep === 'main' ? 'bg-primary text-white' : txProgress === 100 ? 'bg-success text-white' : 'bg-gray-200'}`}>
                  {txProgress === 100 ? '✓' : '2'}
                </div>
                <span className="text-sm mt-1">{config.text.steps.main}</span>
              </div>
            </div>

            {/* Message */}
            <div className="text-center py-3">
              {approvalStatus === ApprovalStatus.CHECKING ? <div className="flex flex-col items-center">
                  <Spinner size="md" color="primary" />
                  <p className="mt-2">{txMessage}</p>
                </div> : <p>{txMessage}</p>}
            </div>

            {/* Transaction hashes */}
            {approvalTxHash && <div className="mt-4 text-sm">
                <div className="font-semibold">Approval Transaction:</div>
                <a href={`${txLink(approvalTxHash, config.chainId)}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                  {approvalTxHash}
                </a>
              </div>}
            
            {mainTxHash && <div className="mt-4 text-sm">
                <div className="font-semibold">Transaction:</div>
                <a href={`${txLink(mainTxHash, config.chainId)}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                  {mainTxHash}
                </a>
              </div>}
          </div>
        </ModalBody>
        
        <ModalFooter>
          {/* Show different buttons based on current state */}
          {error ? <Button color="primary" onPress={handleClose}>
              {config.text.buttons.close}
            </Button> : txProgress === 100 ? <Button color="primary" onPress={handleClose}>
              {config.text.buttons.close}
            </Button> : txStep === 'approval' && (approvalStatus === ApprovalStatus.NEEDS_APPROVAL || approvalStatus === ApprovalStatus.APPROVING) ? <Button color="primary" isLoading={approvalStatus === ApprovalStatus.APPROVING} onPress={handleApprove} isDisabled={approvalStatus === ApprovalStatus.APPROVING}>
              {config.text.buttons.approve}
            </Button> : txStep === 'main' && approvalStatus === ApprovalStatus.APPROVED ? <Button color="primary" isLoading={txProgress > 50 && txProgress < 100} onPress={handleMainTransaction} isDisabled={txProgress > 50}>
              {config.text.buttons.executeMain}
            </Button> : null}
        </ModalFooter>
      </ModalContent>
    </Modal>;
};
export default ApprovalTransactionFlow;